<template>
  <div class="news-page page-body">
    <div class="container">
      <PageHero
        ribbon="News & Updates"
        title="新闻动态"
        subtitle="了解高仁科技最新产品、合作与行业资讯"
        :breadcrumbs="[
          { label: '首页', to: '/' },
          { label: '新闻动态' },
        ]"
      />

      <section class="news-section">
        <div class="filter-row" data-reveal>
          <button
            v-for="cat in newsCategories"
            :key="cat.id"
            type="button"
            class="filter-chip"
            :class="{ 'filter-chip--active': activeCategory === cat.id }"
            @click="activeCategory = cat.id"
          >
            {{ cat.label }}
          </button>
        </div>

        <div v-if="featuredList.length && activeCategory === 'all'" class="featured-row">
          <article
            v-for="(item, idx) in featuredList"
            :key="item.id"
            class="featured-news premium-card"
            data-reveal
            :data-reveal-delay="String(idx + 1)"
          >
            <div class="featured-news__img">
              <img :src="item.image" :alt="item.title" />
              <span v-if="item.isVideo" class="featured-news__play">▶</span>
            </div>
            <div class="featured-news__body">
              <time>{{ formatDate(item.date) }}</time>
              <h2>{{ item.title }}</h2>
              <p>{{ item.summary }}</p>
            </div>
          </article>
        </div>

        <div class="news-grid">
          <article
            v-for="(item, idx) in listItems"
            :key="item.id"
            class="news-card"
            data-reveal
            :data-reveal-delay="String(idx % 4 + 1)"
          >
            <div class="news-card__img">
              <img :src="item.image" :alt="item.title" />
              <span v-if="item.isVideo" class="news-card__badge">视频</span>
            </div>
            <div class="news-card__body">
              <div class="news-card__meta">
                <span class="news-card__cat">{{ categoryLabel(item.category) }}</span>
                <time>{{ formatDate(item.date) }}</time>
              </div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.summary }}</p>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PageHero from '../components/PageHero.vue'
import { newsCategories, newsArticles, getNewsByCategory } from '../data/newsCatalog'
import { useScrollReveal } from '../composables/useScrollReveal'

const { refreshReveal } = useScrollReveal()

const route = useRoute()
const activeCategory = ref('all')

watch(
  () => route.params.category,
  (cat) => {
    activeCategory.value = cat || 'all'
  },
  { immediate: true }
)

const featuredList = computed(() =>
  newsArticles.filter((item) => item.featured).slice(0, 2)
)

const listItems = computed(() => {
  const all = getNewsByCategory(activeCategory.value)
  if (activeCategory.value === 'all') {
    return all.filter((item) => !item.featured)
  }
  return all
})

watch([activeCategory, listItems], async () => {
  await nextTick()
  refreshReveal()
})

function categoryLabel(id) {
  return newsCategories.find((c) => c.id === id)?.label || id
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-')
  return `${y}年${Number(m)}月${Number(d)}日`
}
</script>

<style scoped>
.news-page {
  min-height: 60vh;
}

.news-section {
  padding: 32px 0 64px;
}

.filter-row {
  margin-bottom: 36px;
  justify-content: center;
}

.featured-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 40px;
}

.featured-news {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.featured-news__img {
  position: relative;
  min-height: 200px;
}

.featured-news__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.featured-news__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 36px;
}

.featured-news__body {
  padding: 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.featured-news__body time {
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.featured-news h2 {
  margin: 0 0 12px;
  font-size: 18px;
  line-height: 1.4;
}

.featured-news p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.news-card__img {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
}

.news-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}

.news-card:hover .news-card__img img {
  transform: scale(1.05);
}

.news-card__badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 11px;
  border-radius: var(--radius-sm);
}

.news-card__body {
  padding: 20px;
}

.news-card__meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 12px;
}

.news-card__cat {
  color: var(--brand-primary);
  font-weight: 600;
}

.news-card__meta time {
  color: var(--text-muted);
}

.news-card h3 {
  margin: 0 0 8px;
  font-size: 16px;
  line-height: 1.4;
}

.news-card p {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 960px) {
  .featured-row,
  .featured-news,
  .news-grid {
    grid-template-columns: 1fr;
  }

  .featured-news {
    grid-template-columns: 1fr;
  }
}
</style>
